import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from './admin-api.ts';
import { Card, PrimaryButton, GhostButton, Spinner } from './ui.tsx';
import type { ProvisionResult, RosterInputRow, RosterValidation } from './types.ts';

// ---------- CSV parsing (client-side; the server re-validates everything) ----------
// Accepts a header row (name/email/lob in any order) or positional columns:
// 3 cols = name,email,lob · 2 cols = email,lob.
function parseRoster(text: string): RosterInputRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  const cells = (l: string) => l.split(',').map((c) => c.trim());

  const first = cells(lines[0]!).map((c) => c.toLowerCase());
  const hasHeader = first.includes('email');
  let idx = { name: 0, email: 1, lob: 2 };
  let body = lines;
  if (hasHeader) {
    idx = {
      name: first.indexOf('name'),
      email: first.indexOf('email'),
      lob: first.indexOf('lob') >= 0 ? first.indexOf('lob') : first.indexOf('line of business'),
    };
    body = lines.slice(1);
  }
  return body.map((line) => {
    const c = cells(line);
    if (hasHeader) {
      return { name: idx.name >= 0 ? c[idx.name] ?? null : null, email: c[idx.email] ?? '', lob: idx.lob >= 0 ? c[idx.lob] ?? '' : '' };
    }
    if (c.length >= 3) return { name: c[0] || null, email: c[1] ?? '', lob: c[2] ?? '' };
    return { name: null, email: c[0] ?? '', lob: c[1] ?? '' }; // 2-col: email,lob
  });
}

function statusMeta(status: string): { label: string; cls: string } {
  switch (status) {
    case 'submitted':
    case 'auto_finalized':
      return { label: status === 'auto_finalized' ? 'Auto-finalized' : 'Completed', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' };
    case 'in_progress':
      return { label: 'In progress', cls: 'bg-sky-50 text-sky-700 ring-sky-200' };
    default:
      return { label: 'Not started', cls: 'bg-gray-100 text-gray-600 ring-gray-200' };
  }
}

function downloadCsv(filename: string, rows: string[][]) {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function Tile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-black/5 bg-white p-4 shadow-sm">
      <div className="text-2xl font-bold text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wide text-ink-soft">{label}</div>
    </div>
  );
}

export function RespondentsView({ engagementId }: { engagementId: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [rows, setRows] = useState<RosterInputRow[]>([]);
  const [validation, setValidation] = useState<RosterValidation | null>(null);
  const [provisioned, setProvisioned] = useState<ProvisionResult | null>(null);

  const monitor = useQuery({
    queryKey: ['admin-respondents', engagementId],
    queryFn: () => adminApi.listRespondents(engagementId),
  });

  const validate = useMutation({
    mutationFn: (r: RosterInputRow[]) => adminApi.validateRoster(engagementId, r),
    onSuccess: (v) => {
      setValidation(v);
      setProvisioned(null);
    },
  });
  const provision = useMutation({
    mutationFn: (r: RosterInputRow[]) => adminApi.provisionRespondents(engagementId, r),
    onSuccess: (res) => {
      setProvisioned(res);
      setValidation(null);
      setText('');
      setRows([]);
      void qc.invalidateQueries({ queryKey: ['admin-respondents', engagementId] });
    },
  });
  const regenerate = useMutation({
    mutationFn: (rid: string) => adminApi.regenerateCredentials(engagementId, rid),
  });

  const loginBase = useMemo(() => `${window.location.origin}/`, []);

  const onParse = (value: string) => {
    setText(value);
    setRows(parseRoster(value));
    setValidation(null);
    setProvisioned(null);
  };

  const downloadCredentials = (res: ProvisionResult) => {
    const url = res.slug ? `${loginBase}${res.slug}` : '';
    downloadCsv(`${res.slug ?? 'engagement'}-credentials.csv`, [
      ['name', 'email', 'username', 'password', 'login_url'],
      ...res.credentials.map((c) => [c.name ?? '', c.email, c.username, c.password, url]),
    ]);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Roster upload */}
      <Card title="Add respondents" description="Paste or upload a CSV with columns name, email, LOB (header optional). One row per person.">
        <label className="flex flex-col gap-1 text-sm font-medium text-ink-soft">
          Roster
          <textarea
            className="min-h-32 w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 font-mono text-sm text-ink outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]"
            placeholder={'name,email,lob\nAnn Lee,ann@acme.com,Sales\nBob Ng,bob@acme.com,Operations'}
            value={text}
            onChange={(e) => onParse(e.target.value)}
          />
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            className="text-sm text-ink-soft file:mr-3 file:rounded-lg file:border-0 file:bg-black/5 file:px-3 file:py-1.5 file:text-sm file:font-medium"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) f.text().then(onParse);
            }}
          />
          <span className="text-sm text-ink-soft">{rows.length} row{rows.length === 1 ? '' : 's'} parsed</span>
          <div className="ml-auto flex gap-3">
            <GhostButton onClick={() => rows.length && validate.mutate(rows)}>Validate</GhostButton>
            <PrimaryButton
              onClick={() => rows.length && provision.mutate(rows)}
              disabled={provision.isPending || rows.length === 0}
            >
              {provision.isPending ? 'Provisioning…' : `Provision ${rows.length || ''} respondents`}
            </PrimaryButton>
          </div>
        </div>

        {validate.isPending && <p className="mt-3 text-sm text-ink-soft">Validating…</p>}
        {validation && (
          <div className="mt-4 flex flex-col gap-2 text-sm">
            <p className="text-ink">
              <span className="font-semibold text-emerald-700">{validation.valid.length} ready</span>
              {validation.warnings.length > 0 && <span className="text-amber-700"> · {validation.warnings.length} skipped</span>}
              {validation.errors.length > 0 && <span className="text-red-600"> · {validation.errors.length} with errors</span>}
              {validation.lobsToCreate.length > 0 && (
                <span className="text-ink-soft"> · new LOBs: {validation.lobsToCreate.join(', ')}</span>
              )}
            </p>
            {validation.errors.map((e) => (
              <p key={`e-${e.index}`} className="text-red-600">Row {e.index + 1} ({e.email || 'blank'}): {e.reason}</p>
            ))}
            {validation.warnings.map((w) => (
              <p key={`w-${w.index}`} className="text-amber-700">Row {w.index + 1} ({w.email}): {w.reason}</p>
            ))}
          </div>
        )}
      </Card>

      {/* Generated credentials (shown once) */}
      {provisioned && provisioned.credentials.length > 0 && (
        <Card
          title={`Credentials for ${provisioned.credentials.length} respondents`}
          description="Shown once — passwords are not stored. Download and distribute securely."
          actions={<PrimaryButton onClick={() => downloadCredentials(provisioned)}>Download credentials CSV</PrimaryButton>}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-black/10 text-xs uppercase tracking-wide text-ink-soft">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Username</th>
                  <th className="py-2 pr-4">Password</th>
                </tr>
              </thead>
              <tbody>
                {provisioned.credentials.map((c) => (
                  <tr key={c.username} className="border-b border-black/5 last:border-0">
                    <td className="py-2 pr-4 text-ink-soft">{c.email}</td>
                    <td className="py-2 pr-4 font-medium text-ink">{c.username}</td>
                    <td className="py-2 pr-4 font-mono text-ink">{c.password}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Monitoring */}
      <Card title="Response monitoring" description="Live completion status across all respondents in this engagement.">
        {monitor.isLoading ? (
          <Spinner />
        ) : monitor.isError || !monitor.data ? (
          <p className="py-6 text-center text-sm text-red-600" role="alert">Could not load respondents.</p>
        ) : monitor.data.summary.total === 0 ? (
          <p className="py-6 text-center text-sm text-ink-soft">No respondents yet. Add some above.</p>
        ) : (
          <>
            <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Tile label="Total" value={monitor.data.summary.total} />
              <Tile label="Not started" value={monitor.data.summary.notStarted} />
              <Tile label="In progress" value={monitor.data.summary.inProgress} />
              <Tile label="Completed" value={monitor.data.summary.completed} />
              <Tile label="Completion" value={`${monitor.data.summary.completionRate}%`} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-xs uppercase tracking-wide text-ink-soft">
                    <th className="py-2 pr-4">Respondent</th>
                    <th className="py-2 pr-4">LOB</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {monitor.data.respondents.map((r) => {
                    const m = statusMeta(r.status);
                    return (
                      <tr key={r.id} className="border-b border-black/5 last:border-0">
                        <td className="py-2 pr-4">
                          <div className="font-medium text-ink">{r.displayName ?? r.email ?? '—'}</div>
                          {r.displayName && r.email && <div className="text-xs text-ink-soft">{r.email}</div>}
                        </td>
                        <td className="py-2 pr-4 text-ink-soft">{r.lob}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${m.cls}`}>{m.label}</span>
                        </td>
                        <td className="py-2 pr-4 text-right">
                          <button
                            type="button"
                            onClick={() => regenerate.mutate(r.id, {
                              onSuccess: (cred) =>
                                downloadCsv(`credential-${cred.username}.csv`, [
                                  ['email', 'username', 'password', 'login_url'],
                                  [r.email ?? '', cred.username, cred.password, `${loginBase}${monitor.data?.slug ?? ''}`],
                                ]),
                            })}
                            className="text-xs font-medium text-[var(--accent)] underline-offset-4 hover:underline disabled:opacity-50"
                            disabled={regenerate.isPending}
                          >
                            Regenerate
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
